import { useCallback, useMemo } from "react"

import { METADATA_FILTER_MATCH_TYPE } from "@/constants/filter-types"

import { TRACKING_EVENTS, type TrackingEventName } from "@/lib/analytics"
import type { MetadataFilter } from "@/types/MetadataFilter"

import type { MetricValueFilter } from "../types"
import type { SortDescriptor } from "../types/sortDescriptor"

interface UrlSyncedState {
  dateRange: { start: Date | null; end: Date | null }
  metricIds: string[]
  agentIds: string[]
  tagFilters: string[]
  projectIds: string[]
  metricValueFilters: MetricValueFilter[]
  metadataFilters: MetadataFilter[]
}

interface UseConversationsFilterActionsProps {
  urlState: UrlSyncedState
  setDateRange: (value: UrlSyncedState["dateRange"]) => void
  setMetadataFiltersField: (value: MetadataFilter[]) => void
  updateMetadataFilters: (
    updater: (current: MetadataFilter[]) => MetadataFilter[],
  ) => void
  setMetricValueFiltersField: (value: MetricValueFilter[]) => void
  updateMetricValueFilters: (
    updater: (current: MetricValueFilter[]) => MetricValueFilter[],
  ) => void
  setMetricIdsField: (value: string[]) => void
  setAgentIdsField: (value: string[]) => void
  setTagFiltersField: (value: string[]) => void
  setProjectIdsField: (value: string[]) => void
  resetUrlFilters: () => void
  setSelectedMetric: (metric: string) => void
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void
  trackEvent: (
    event: TrackingEventName,
    properties?: Record<string, string | number | boolean | null | undefined>,
  ) => void
}

export function useConversationsFilterActions({
  urlState,
  setDateRange,
  setMetadataFiltersField,
  updateMetadataFilters,
  setMetricValueFiltersField,
  updateMetricValueFilters,
  setMetricIdsField,
  setAgentIdsField,
  setTagFiltersField,
  setProjectIdsField,
  resetUrlFilters,
  setSelectedMetric,
  setSortDescriptor,
  trackEvent,
}: UseConversationsFilterActionsProps) {
  const totalActiveFilters = useMemo(() => {
    let count = 0
    if (urlState.dateRange.start || urlState.dateRange.end) {
      count++
    }
    count += urlState.metadataFilters.length
    count += urlState.metricValueFilters.length
    count += urlState.metricIds.length
    count += urlState.agentIds.length
    count += urlState.tagFilters.length
    count += urlState.projectIds.length
    return count
  }, [
    urlState.dateRange.start,
    urlState.dateRange.end,
    urlState.metadataFilters.length,
    urlState.metricValueFilters.length,
    urlState.metricIds.length,
    urlState.agentIds.length,
    urlState.tagFilters.length,
    urlState.projectIds.length,
  ])

  const hasActiveFilters = totalActiveFilters > 0

  const handlerSetDateRange = useCallback(
    (start: Date | null, end: Date | null) => {
      setDateRange({ start, end })
      trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.DATE_RANGE_CHANGED, {
        has_start_date: !!start,
        has_end_date: !!end,
        start_date: start?.toISOString(),
        end_date: end?.toISOString(),
        range_days:
          start && end
            ? Math.ceil(
                (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
              )
            : null,
      })
    },
    [setDateRange, trackEvent],
  )

  const handlerClearDateRange = useCallback(() => {
    setDateRange({ start: null, end: null })
    trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.FILTER_CLEARED, {
      filter_type: "date_range",
    })
  }, [setDateRange, trackEvent])

  const handlerSetMetadataFilters = useCallback(
    (filters: MetadataFilter[]) => {
      setMetadataFiltersField(filters)
    },
    [setMetadataFiltersField],
  )

  const handlerAddMetadataFilter = useCallback(
    (
      key: string,
      value: string,
      matchType: MetadataFilter["matchType"] = METADATA_FILTER_MATCH_TYPE.EQUALS,
      values?: string[],
    ) => {
      updateMetadataFilters((current) => [
        ...current,
        {
          key,
          value,
          matchType,
          ...(matchType === "ANY_OF" ? { values } : {}),
        },
      ])
      trackEvent(
        TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METADATA_FILTER_APPLIED,
        {
          filter_key: key,
          filter_value: value,
          match_type: matchType,
        },
      )
    },
    [trackEvent, updateMetadataFilters],
  )

  const handlerRemoveMetadataFilter = useCallback(
    (index: number) => {
      updateMetadataFilters((current) =>
        current.filter((_, currentIndex) => currentIndex !== index),
      )
    },
    [updateMetadataFilters],
  )

  const handlerClearMetadataFilters = useCallback(() => {
    setMetadataFiltersField([])
  }, [setMetadataFiltersField])

  const handlerAddMetricValueFilter = useCallback(
    (filter: MetricValueFilter) => {
      updateMetricValueFilters((current) => {
        const filtered = current.filter(
          (item) => item.metricId !== filter.metricId,
        )
        return [...filtered, filter]
      })
      trackEvent(
        TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METRIC_VALUE_FILTER_APPLIED,
        {
          metric_id: filter.metricId,
          metric_name: filter.metricDisplayName,
          operator: filter.operator,
          value: filter.value,
          value_type: filter.valueType,
        },
      )
    },
    [trackEvent, updateMetricValueFilters],
  )

  const handlerRemoveMetricValueFilter = useCallback(
    (metricId: string) => {
      updateMetricValueFilters((current) =>
        current.filter((filter) => filter.metricId !== metricId),
      )
      trackEvent(
        TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METRIC_VALUE_FILTER_REMOVED,
        {
          metric_id: metricId,
        },
      )
    },
    [trackEvent, updateMetricValueFilters],
  )

  const handlerClearMetricValueFilters = useCallback(() => {
    setMetricValueFiltersField([])
    trackEvent(
      TRACKING_EVENTS.UPLOADED_CONVERSATIONS.METRIC_VALUE_FILTERS_CLEARED,
      {},
    )
  }, [setMetricValueFiltersField, trackEvent])

  const handlerSetSelectedMetric = useCallback(
    (metric: string) => {
      setSelectedMetric(metric)
    },
    [setSelectedMetric],
  )

  const handlerSetSortDescriptor = useCallback(
    (sortDescriptor: SortDescriptor) => {
      setSortDescriptor(sortDescriptor)
    },
    [setSortDescriptor],
  )

  const setMetricIds = useCallback(
    (metricIds: string[]) => {
      setMetricIdsField(metricIds)
    },
    [setMetricIdsField],
  )

  const setAgentIds = useCallback(
    (agentIds: string[]) => {
      setAgentIdsField(agentIds)
    },
    [setAgentIdsField],
  )

  const setTagFilters = useCallback(
    (tagFilters: string[]) => {
      setTagFiltersField(tagFilters)
    },
    [setTagFiltersField],
  )

  const setProjectIds = useCallback(
    (projectIds: string[]) => {
      setProjectIdsField(projectIds)
    },
    [setProjectIdsField],
  )

  const clearAllFilters = useCallback(() => {
    resetUrlFilters()
    setSelectedMetric("")
  }, [resetUrlFilters, setSelectedMetric])

  return {
    totalActiveFilters,
    hasActiveFilters,
    handlerSetDateRange,
    handlerClearDateRange,
    handlerSetMetadataFilters,
    handlerAddMetadataFilter,
    handlerRemoveMetadataFilter,
    handlerClearMetadataFilters,
    handlerAddMetricValueFilter,
    handlerRemoveMetricValueFilter,
    handlerClearMetricValueFilters,
    handlerSetSelectedMetric,
    handlerSetSortDescriptor,
    setMetricIds,
    setAgentIds,
    setTagFilters,
    setProjectIds,
    clearAllFilters,
  }
}
